import { useState, useId } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	FeedbackService,
	type FeedbackCategory,
	type CreateFeedbackRequest,
} from "@/lib/feedback/feedback.service";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Props for the FeedbackModal component
 */
interface FeedbackModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/**
 * Modal component for submitting user feedback
 * Features:
 * - Multiple feedback categories
 * - Title and detailed description
 * - Automatic page context capture
 * - Form validation
 * - Loading states
 * - Success/error handling
 */
export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
	const { t } = useTranslation();
	const location = useLocation();
	const categoryId = useId();
	const titleId = useId();
	const descriptionId = useId();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formData, setFormData] = useState<{
		title: string;
		description: string;
		category: FeedbackCategory;
	}>({
		title: "",
		description: "",
		category: "OTHER",
	});

	const [errors, setErrors] = useState<{
		title?: string;
		description?: string;
	}>({});

	/**
	 * Feedback category options
	 */
	const FEEDBACK_CATEGORIES: Array<{
		value: FeedbackCategory;
		label: string;
		icon: string;
	}> = [
		{ value: "BUG", label: t("feedback.categories.BUG"), icon: "🐛" },
		{
			value: "FEATURE_REQUEST",
			label: t("feedback.categories.FEATURE_REQUEST"),
			icon: "💡",
		},
		{
			value: "IMPROVEMENT",
			label: t("feedback.categories.IMPROVEMENT"),
			icon: "✨",
		},
		{ value: "UX", label: t("feedback.categories.UX"), icon: "🎨" },
		{
			value: "PERFORMANCE",
			label: t("feedback.categories.PERFORMANCE"),
			icon: "⚡",
		},
		{ value: "OTHER", label: t("feedback.categories.OTHER"), icon: "💬" },
	];

	/**
	 * Validate form inputs
	 */
	const validateForm = (): boolean => {
		const newErrors: typeof errors = {};

		if (!formData.title.trim()) {
			newErrors.title = t("feedback.titleRequired");
		} else if (formData.title.length > 200) {
			newErrors.title = t("feedback.titleTooLong");
		}

		if (!formData.description.trim()) {
			newErrors.description = t("feedback.descriptionRequired");
		} else if (formData.description.length > 5000) {
			newErrors.description = t("feedback.descriptionTooLong");
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	/**
	 * Handle form submission
	 */
	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			const feedbackService = FeedbackService.getInstance();

			// Prepare feedback data
			const feedbackData: CreateFeedbackRequest = {
				title: formData.title.trim(),
				description: formData.description.trim(),
				category: formData.category,
				currentPage: location.pathname,
				metadata: {
					userAgent: navigator.userAgent,
					screenWidth: window.innerWidth,
					screenHeight: window.innerHeight,
					timestamp: new Date().toISOString(),
				},
			};

			// Submit feedback
			await feedbackService.submitFeedback(feedbackData);

			// Show success message
			toast.success(t("feedback.successTitle"), {
				description: t("feedback.successMessage"),
			});

			// Reset form and close modal
			setFormData({
				title: "",
				description: "",
				category: "OTHER",
			});
			setErrors({});
			onOpenChange(false);
		} catch (error) {
			console.error("Failed to submit feedback:", error);

			// Show error message
			const errorMessage =
				error &&
				typeof error === "object" &&
				"response" in error &&
				error.response &&
				typeof error.response === "object" &&
				"data" in error.response &&
				error.response.data &&
				typeof error.response.data === "object" &&
				"error" in error.response.data
					? String(error.response.data.error)
					: t("feedback.errorMessage");

			toast.error(t("feedback.errorTitle"), {
				description: errorMessage,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	/**
	 * Handle modal close
	 */
	const handleClose = (open: boolean) => {
		if (!open && !isSubmitting) {
			// Reset form when closing
			setFormData({
				title: "",
				description: "",
				category: "OTHER",
			});
			setErrors({});
		}
		onOpenChange(open);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[500px] z-[100]"
				overlayClassName="z-[100]"
			>
				<DialogHeader>
					<DialogTitle>{t("feedback.title")}</DialogTitle>
					<DialogDescription>{t("feedback.description")}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4 py-4">
					{/* Category Selection */}
					<div className="flex flex-col gap-2">
						<Label htmlFor={categoryId}>{t("feedback.categoryLabel")}</Label>
						<Select
							value={formData.category}
							onValueChange={(value) =>
								setFormData({
									...formData,
									category: value as FeedbackCategory,
								})
							}
						>
							<SelectTrigger id={categoryId} className="w-full">
								<SelectValue placeholder={t("feedback.categoryPlaceholder")} />
							</SelectTrigger>
							<SelectContent>
								{FEEDBACK_CATEGORIES.map((cat) => (
									<SelectItem key={cat.value} value={cat.value}>
										<span className="flex items-center gap-2">
											<span>{cat.icon}</span>
											<span>{cat.label}</span>
										</span>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Title Input */}
					<div className="flex flex-col gap-2">
						<Label htmlFor={titleId}>
							{t("feedback.titleLabel")}{" "}
							<span className="text-destructive">{t("feedback.required")}</span>
						</Label>
						<Input
							id={titleId}
							placeholder={t("feedback.titlePlaceholder")}
							value={formData.title}
							onChange={(e) =>
								setFormData({ ...formData, title: e.target.value })
							}
							aria-invalid={!!errors.title}
							maxLength={200}
							disabled={isSubmitting}
						/>
						{errors.title && (
							<p className="text-sm text-destructive">{errors.title}</p>
						)}
						<p className="text-xs text-muted-foreground">
							{t("feedback.charactersCount", {
								count: formData.title.length,
								max: 200,
							})}
						</p>
					</div>

					{/* Description Textarea */}
					<div className="flex flex-col gap-2">
						<Label htmlFor={descriptionId}>
							{t("feedback.descriptionLabel")}{" "}
							<span className="text-destructive">{t("feedback.required")}</span>
						</Label>
						<Textarea
							id={descriptionId}
							placeholder={t("feedback.descriptionPlaceholder")}
							value={formData.description}
							onChange={(e) =>
								setFormData({ ...formData, description: e.target.value })
							}
							aria-invalid={!!errors.description}
							rows={6}
							maxLength={5000}
							disabled={isSubmitting}
							className="resize-none"
						/>
						{errors.description && (
							<p className="text-sm text-destructive">{errors.description}</p>
						)}
						<p className="text-xs text-muted-foreground">
							{t("feedback.charactersCount", {
								count: formData.description.length,
								max: 5000,
							})}
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => handleClose(false)}
						disabled={isSubmitting}
					>
						{t("common.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								{t("feedback.submitting")}
							</>
						) : (
							t("feedback.submitButton")
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
